/**
 * Hub demo launcher view model.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  entryHubPath,
  entryLocalUrl,
  entrySlug,
  isHubInternalEntry,
  isWorkspaceEntry,
  type DemoCatalogEntry,
} from "../../catalog/catalog-data.js";
import launcherVisuals from "../../catalog/demo-launcher-visuals.data.json" with { type: "json" };
import { PRIMARY } from "../../design-system/tokens.js";
import type { KpiCardGridItem } from "../charts/KpiCardGrid.js";
import type { StatusPillTone } from "../StatusPill.js";

export type HubLauncherFilter = "all" | "hub" | "fallback";

export interface HubLauncherDemoView {
  readonly entry: DemoCatalogEntry;
  readonly slug: string;
  readonly wired: boolean;
  readonly accent: string;
  readonly topologyLabel: string;
  readonly topologyTone: StatusPillTone;
  readonly actionLabel: string;
  readonly actionHref: string;
  readonly command: string;
  readonly searchText: string;
}

const ACCENTS: readonly string[] = launcherVisuals.accents;

export function accentFor(slug: string): string {
  let hash = 0;
  for (let index = 0; index < slug.length; index += 1) {
    hash = (hash * 31 + slug.charCodeAt(index)) | 0;
  }
  return ACCENTS[Math.abs(hash) % ACCENTS.length] ?? PRIMARY[500];
}

export function isHubReachableView(view: HubLauncherDemoView): boolean {
  return view.wired || isHubInternalEntry(view.entry);
}

function topologyLabel(entry: DemoCatalogEntry, wired: boolean): string {
  if (isHubInternalEntry(entry)) return "hub-native";
  if (wired) return "hub route";
  return `port ${String(entry.port)}`;
}

function topologyTone(entry: DemoCatalogEntry, wired: boolean): StatusPillTone {
  if (wired || isHubInternalEntry(entry)) return "safe";
  return "neutral";
}

function actionLabel(entry: DemoCatalogEntry, wired: boolean): string {
  if (wired || isHubInternalEntry(entry)) return "Open in hub";
  return "Open per-port";
}

function actionHref(entry: DemoCatalogEntry, wired: boolean): string {
  if (wired || isHubInternalEntry(entry)) return entryHubPath(entry);
  return entryLocalUrl(entry) ?? entryHubPath(entry);
}

function commandLine(entry: DemoCatalogEntry, wired: boolean): string {
  if (wired || isHubInternalEntry(entry)) return entryHubPath(entry);
  if (isWorkspaceEntry(entry)) return `npm run ${entry.script}`;
  return entryHubPath(entry);
}

export function toHubLauncherDemoView(
  entry: DemoCatalogEntry,
  wiredSlugs: ReadonlySet<string>,
): HubLauncherDemoView {
  const slug = entrySlug(entry);
  const wired = wiredSlugs.has(slug);
  return {
    entry,
    slug,
    wired,
    accent: accentFor(slug),
    topologyLabel: topologyLabel(entry, wired),
    topologyTone: topologyTone(entry, wired),
    actionLabel: actionLabel(entry, wired),
    actionHref: actionHref(entry, wired),
    command: commandLine(entry, wired),
    searchText: [entry.title, entry.blurb, entry.summary, slug].join(" ").toLowerCase(),
  };
}

export function launcherQueryTerms(query: string): readonly string[] {
  const normalized = query.toLowerCase().trim();
  if (normalized.length === 0) return [];
  return normalized.split(/\s+/).filter((term) => term.length > 0);
}

export function matchesLauncherTerms(view: HubLauncherDemoView, terms: readonly string[]): boolean {
  return terms.length === 0 || terms.every((term) => view.searchText.includes(term));
}

export function matchesLauncherQuery(view: HubLauncherDemoView, query: string): boolean {
  return matchesLauncherTerms(view, launcherQueryTerms(query));
}

export function matchesLauncherFilter(
  view: HubLauncherDemoView,
  filter: HubLauncherFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "hub") return isHubReachableView(view);
  return !isHubReachableView(view);
}

export function coverageItems(wiredCount: number, totalCount: number): readonly KpiCardGridItem[] {
  const coverage = totalCount === 0 ? 0 : Math.round((wiredCount / totalCount) * 100);
  const remaining = Math.max(0, totalCount - wiredCount);
  return [
    {
      id: "wired",
      label: "Hub routes",
      value: wiredCount,
      tone: "safe",
      caption: "Catalog entries opened inside the hub.",
    },
    {
      id: "coverage",
      label: "Coverage",
      value: coverage,
      valueSuffix: "%",
      tone: coverage >= 90 ? "safe" : "info",
      caption: "Share of catalog wired into the route table.",
    },
    {
      id: "remaining",
      label: "Per-port fallback",
      value: remaining,
      tone: remaining === 0 ? "safe" : "warn",
      caption: "Workspace entries still served on fixed ports.",
    },
  ];
}
